import type { MenuTreeListItem } from '@/api/role/types'
import { type RouteRecordRaw } from 'vue-router'

// 菜单项接口
export interface MenuItem {
  name: string
  path: string
  meta: {
    title: string
    icon?: string
    sort?: number
    hidden?: boolean
    [key: string]: unknown
  }
  children?: MenuItem[]
}

// 排序函数
export const sortMenuItems = (items: MenuItem[]): MenuItem[] => {
  return items
    .filter((item) => !item.meta.hidden) // 过滤隐藏的菜单
    .sort((a, b) => {
      const aSort = a.meta.sort ?? 999 // 默认值确保未设置sort的排在后面
      const bSort = b.meta.sort ?? 999
      return aSort - bSort
    })
    .map((item) => {
      if (item.children) {
        return {
          ...item,
          children: sortMenuItems(item.children),
        }
      }
      return item
    })
}

// 将路由转换为菜单项
export const routesToMenuItems = (routes: RouteRecordRaw[]): MenuItem[] => {
  return routes.map((route) => ({
    name: route.name as string,
    path: route.path,
    meta: {
      ...route.meta,
      title: (route.meta?.title as string) || '',
      sort: (route.meta?.sort as number) || 999,
    },
    children: route.children ? routesToMenuItems(route.children) : undefined,
  }))
}

export const pathToCamel = (path: string) => {
  return path
    .split('/') // 按斜杠分割
    .filter(Boolean) // 去掉空字符串（因为开头有 /）
    .map((word: string, index: number) =>
      index === 0
        ? word // 第一个单词保持小写
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join('')
}

export const pathToFirstSegment = (path: string) => {
  return path
    .split('/') // 按斜杠分割
    .filter(Boolean)[0] // 去掉空字符串 // 取第一个有效段
}

/** 去掉最后一段，得到目录 path：/health/archive/user → /health/archive */
export const pathToDirPath = (path: string) => {
  const parts = path.split('/').filter(Boolean)
  if (parts.length <= 1) return parts[0] ? `/${parts[0]}` : ''
  return `/${parts.slice(0, -1).join('/')}`
}

/** 目录 path 转 camelCase：/health/archive/user → healthArchive */
export const pathToDirCamel = (path: string) => {
  return pathToCamel(pathToDirPath(path))
}

const isRootMenu = (menu: MenuTreeListItem) => String(menu.parentId) === '0'

const normalizePath = (path: string) => {
  if (!path) return ''
  return path.startsWith('/') ? path : `/${path}`
}

/**
 * 一级目录的 path 仅用于展示/兜底，展开 key 必须用 menuId，不能用 path 推导。
 * 运营管理、运维管理如果 path 都是 /operation，或子路由都挂在 /operation/* 下，
 * 以前会得到同一个 key「operation」，Ant Design Menu 就会一起展开。
 */
const resolveRootMenuPath = (menu: MenuTreeListItem, firstChildPath: string) => {
  const selfPath = normalizePath(menu.path?.trim() || '')
  const selfParts = selfPath.split('/').filter(Boolean)

  if (selfParts.length >= 2) return selfPath

  const dirPath = pathToDirPath(firstChildPath)
  if (dirPath) return dirPath

  const first = pathToFirstSegment(firstChildPath || selfPath)
  return first ? `/${first}` : selfPath || '/'
}

const resolveRootMenuName = (menu: MenuTreeListItem, fallbackPath: string) => {
  if (menu.menuId != null && String(menu.menuId) !== '') {
    return `root_${menu.menuId}`
  }
  return pathToCamel(fallbackPath) || pathToFirstSegment(fallbackPath) || 'menu'
}

export const menuToMenuItems = (menus: MenuTreeListItem[]): MenuItem[] => {
  return menus.map((menu) => {
    const hasChildren = Boolean(menu.children?.length)
    const firstChildPath =
      menu.children?.[0]?.path ||
      menu.children?.[0]?.children?.[0]?.path ||
      menu.path ||
      ''

    let name: string
    let path: string

    if (isRootMenu(menu) && hasChildren) {
      path = resolveRootMenuPath(menu, firstChildPath)
      name = resolveRootMenuName(menu, path)
    } else if (isRootMenu(menu)) {
      path = normalizePath(menu.path) || `/${pathToCamel(menu.path) || 'menu'}`
      name = resolveRootMenuName(menu, path)
    } else {
      // 非一级：path 转 camelCase，必须与 router name 一致
      name = pathToCamel(menu.path)
      path = menu.path
    }

    return {
      name,
      path,
      redirect: hasChildren
        ? menu.children?.[0]?.path || menu.children?.[0]?.children?.[0]?.path || ''
        : '',
      meta: {
        icon: menu.icon,
        title: (menu.menuName as string) || '',
        sort: (menu.sort as number) || 999,
      },
      children: hasChildren ? menuToMenuItems(menu.children!) : undefined,
    }
  })
}
